/**
 * Firebase Analytics イベント辞書（SSOT: 2026-08-13_イベント計測実装依頼書 + データチーム修正）。
 * ここに無いイベント名・パラメータ名・enum値は追加禁止（追加はデータチームへ連絡）。
 *
 * 型ルール（依頼書 §1 ルール4）:
 * - ID系は string（数値でも文字列化）
 * - 金額・秒数・件数は int（number）
 * - 真偽値は 0/1 の int（boolean 禁止）
 * - enum は小文字 snake_case の string
 */

import type { BannerType } from "~/features/banner/types";

/** パラメータに使える型。boolean はコンパイルエラーになる */
export type FirebaseParamValue = string | number;

export type FirebaseEventParams = Record<string, FirebaseParamValue>;

export type FirebaseCallType = "voice" | "video";

/** 発信が繋がらなかった理由（call_fallback_step の trigger）。cancelled は応答前に自分で切ったもの */
export type FirebaseCallFallbackTrigger =
  | "no_answer"
  | "rejected"
  | "cancelled";

export type FirebaseCallResult =
  | "answered"
  | "rejected"
  | "no_answer"
  | "busy"
  | "unavailable"
  | "error";

export type FirebaseCallEndReason =
  | "hangup_self"
  | "hangup_partner"
  | "pt_exhausted"
  | "network"
  | "timeout";

export type FirebaseBroadcastEndReason =
  | "self"
  | "network"
  | "crash"
  | "banned"
  | "timeout"
  | "connect_failed";

export type FirebaseBroadcastExitReason =
  | "self"
  | "broadcast_ended"
  | "background"
  | "network"
  | "pt_exhausted";

export type FirebaseHeartbeatState =
  | "waiting"
  | "broadcasting"
  | "calling"
  | "viewing";

export type FirebaseAvailabilityState = "offline" | "online" | "available";

export type FirebasePaywallTrigger = "pt_zero" | "self" | "campaign";

/** 課金画面の決済タブ（実UIの3タブ。PAY_TABS と対応） */
export type FirebasePayTab = "iap" | "web" | "paidy";

/**
 * 課金画面（web タブ）へ誘導する入口。課金画面を開くときの source（URL）と同じ語彙。
 * - discover_list_banner: さがす女性一覧の Apple Pay・クレカ決済バナー
 * - recommend_notice: おすすめ画面ヘッダーのお知らせ「ポイント購入が10％お得！」
 */
export type FirebasePurchasePromo = "discover_list_banner" | "recommend_notice";

/** camelCase → snake_case の型。BannerType の値を banner_type の語彙（snake_case）に写す */
type CamelToSnake<S extends string> = S extends `${infer H}${infer T}`
  ? `${H extends Lowercase<H> ? H : `_${Lowercase<H>}`}${CamelToSnake<T>}`
  : S;

/** BannerType 以外の banner_type（固定バナー・購入画面のスライド・ポイントバックのカード） */
export type FirebaseExtraBannerType =
  | "deal"
  | "campaign"
  | "purchase_bonus"
  | "purchase_web_payment"
  | "purchase_paidy"
  | "purchase_daily_bonus"
  | "new_user_point_back"
  | "autumn_point_back";

/** banner_tap の banner_type。全て小文字 snake_case（BannerType は camelCase から変換する） */
export type FirebaseBannerType =
  | CamelToSnake<BannerType>
  | FirebaseExtraBannerType;

/**
 * バナーの設置場所（banner_tap の placement）。
 * - discover_header / recommend_header: さがす画面・おすすめ画面ヘッダーのバナー
 * - my_page_footer: マイページのフッターのバナー
 * - news_page / news_modal: お知らせ画面・お知らせモーダルのバナー
 * - daily_bonus: デイリーボーナス画面のバナー
 * - purchase_carousel: 購入画面上部のバナー
 * - my_page_point_back_card: マイページのポイントバック進捗カード
 */
export type FirebaseBannerPlacement =
  | "discover_header"
  | "recommend_header"
  | "my_page_footer"
  | "news_page"
  | "news_modal"
  | "daily_bonus"
  | "purchase_carousel"
  | "my_page_point_back_card";

/**
 * 課金導線が発生したシーン（pt_zero_reached.last_action と同一語彙）。
 * ライブ配信中/通話中に自発的に開いたチャージシート由来の課金を、通常の
 * /purchase ページ由来と区別するために paywall_view / purchase_plan_tap に付与する。
 * 未指定 = 通常の /purchase ページから。
 */
export type FirebasePurchaseScene =
  | "videocall"
  | "voicecall"
  | "live"
  | "chat"
  | "gift"
  | "media_image"
  | "media_video"
  | "media_audio";

/**
 * A/Bテストの群。割り付けは BE（`enroll_*_experiment`）が user_id から決定的に決める。
 * 語彙は BE の到達ログ（`logdb.*_experiment_enrollment` の variant）と揃えること。
 */
export type FirebaseAbVariant = "control" | "treatment";

/** iOS の ATT 許可状態（Native の 0〜3 を変換）。応答が無ければ unknown */
export type FirebaseAttStatus =
  | "not_determined"
  | "restricted"
  | "denied"
  | "authorized"
  | "unknown";

/** push 通知許諾の事前案内シートを出したきっかけ */
export type FirebasePushPromptTrigger =
  | "chat_revisit"
  | "favorite_add"
  | "header_mail";

/** push 通知許諾ダイアログを出したきっかけ（push_permission_change.trigger）。Sina 独自追加（2026-10-20） */
export type FirebasePushPermissionTrigger =
  | FirebasePushPromptTrigger
  | "discover_open";

/** push 通知許諾の結果（Native の応答を変換）。応答が無ければ unknown */
export type FirebasePushStatus = "granted" | "denied" | "unknown";

/** 新規登録導線（処置群）の画面。signup_flow_step.step の値 */
export type FirebaseSignupFlowStep =
  | "welcome"
  | "intent"
  | "nudge"
  | "form"
  | "register_complete";

export type FirebasePreferenceOnboardingQuestion =
  | "age"
  | "body_type"
  | "bust_size"
  | "personality"
  | "chat_style"
  | "step_to_call"
  | "showing_face"
  | "silent_ok"
  | "animal_face";

export type FirebaseLovenseControlType = "toy" | "full";

/** 配信の視聴種別（broadcast_preview_view / broadcast_watch_tap） */
export type FirebaseBroadcastWatchType = "paid" | "free";

/** メッセージ一覧で配信中（live）か配信待機中（standby）か（message_list_live_tap）。Sina 独自、2026-11-04 追加。データチーム承認待ち */
export type FirebaseMessageListLiveStatus = "live" | "standby";

/** さがす画面のセクション（profile_view / discover_section_view）。Sina 独自、2026-10-13 追加 */
export type FirebaseProfileViewSection =
  | "talked_before"
  | "instant_call"
  | "high_review"
  | "preference_match"
  | "meet_people"
  // おすすめ画面の S / M な性格の女のコ・ビデオ通話ランキング（Sina 独自、2026-10-29 追加。データチーム承認待ち）
  | "sadistic"
  | "masochistic"
  | "ranking";

export type FirebaseBustSize =
  | "a_cup"
  | "b_cup"
  | "c_cup"
  | "d_cup"
  | "e_cup"
  | "big_breasts";

/**
 * 画面ID一覧（依頼書 §4 + 修正: おすすめ=recommend / 探す=find / 検索機能=search）。
 * この値以外を screen_view に入れない。クラス名・ルートパスは禁止。
 */
export const FIREBASE_SCREEN_ID = {
  RECOMMEND: "recommend",
  FIND: "find",
  SEARCH: "search",
  BOARD: "board",
  FOOTSTEP: "footstep",
  RANKING: "ranking",
  GALLERY: "gallery",
  FAVORITE_LIST: "favorite_list",
  LIKE_LIST: "like_list",
  CALL_HISTORY: "call_history",
  FLEAMARKET: "fleamarket",
  PROFILE_SELF: "profile_self",
  CHAT: "chat",
  PAYWALL: "paywall",
  WAITING: "waiting",
} as const;

export type FirebaseScreenId =
  (typeof FIREBASE_SCREEN_ID)[keyof typeof FIREBASE_SCREEN_ID];

/**
 * イベント名 → パラメータ型のマップ。source は共通モジュールが自動付与するため含めない。
 * （screen_view はここに含めず trackFirebaseScreenView 専用）
 */
export type FirebaseEventParamsMap = {
  // §9 その他
  // ab_variant は Carina の新規登録導線 A/B（signupFlowExperiment）の群（2026-10-15 追加）。
  // 割り付け前・Carina 以外では付かない
  signup_start: { method: string; ab_variant?: FirebaseAbVariant };
  signup_complete: { method: string; ab_variant?: FirebaseAbVariant };
  /**
   * 新規登録導線 A/B の処置群で、登録前後の各画面に初めて着いたとき（2026-10-15 追加）。
   * signup_start → signup_complete の間のどこで抜けたかを見る。
   * intents は step=nudge のときだけ付く、志向の画面で選んだもの（call / chat / live をカンマ区切り）
   */
  signup_flow_step: {
    step: FirebaseSignupFlowStep;
    ab_variant: FirebaseAbVariant;
    intents?: string;
  };
  /** 処置群の登録完了画面で選んだ行き先（2026-10-15 追加） */
  signup_flow_choice: { choice: "start_now" | "diagnose" };
  profile_edit: { field: "about_me" | "age" | "avatar" };
  // section / position はさがす画面のセクション経由のときだけ付く（Sina 独自、2026-10-13 追加）。
  // position はセクション内の 0 始まりのカード位置で、section と必ずセットで送る
  profile_view: {
    partner_id: string;
    section?: FirebaseProfileViewSection;
    position?: number;
  };
  // さがす画面のマウントごとに、各セクションが初めて表示領域に入ったとき 1 回だけ送る（Sina 独自、2026-10-13 追加）
  discover_section_view: { section: FirebaseProfileViewSection };
  // メッセージ一覧で、配信中・配信待機中の相手のプロフィール画像を押して配信用プロフィールへ進んだ（Sina 独自、2026-11-04 追加。データチーム承認待ち）。
  // 配信用プロフィールの表示は broadcast_preview_view、視聴ボタンは broadcast_watch_tap で取れるため、ここは一覧からの流入だけを取る
  message_list_live_tap: {
    partner_id: string;
    live_status: FirebaseMessageListLiveStatus;
  };
  // プロフ画像タップで拡大表示した操作。配信視聴/通話中プロフィール等、複数画面から
  // 共通コンポーネント経由で発火するため画面種別は区別しない
  profile_avatar_tap: { partner_id: string };
  // 修正#3: conversation_id は廃止、partner_id のみ
  chat_open: { partner_id: string };
  /**
   * チャットの入力欄（2026-10-07 共通辞書に追加。データチーム依頼）。
   * 「書きかけて送らなかった」= pt を使う一歩手前で止まった人を取るため。送信の成立（pt 消費）は送らない（log_point が正）。
   * input_start の後は send_tap か input_abandon のどちらか 1 回だけ（chatComposeAnalytics.ts）
   */
  // 空の入力欄に文字が入った（送信後・全部消した後にまた入れたら再送）
  chat_input_start: { partner_id: string };
  // 送信ボタンを押した（成否は問わない）
  chat_send_tap: { partner_id: string };
  // 文字が入ったまま送らずに、ルームを離れた・アプリを裏に回した・全部消した
  chat_input_abandon: { partner_id: string };
  search_execute: {
    /** 使った検索条件をアルファベット順・パイプ区切りで（例: "age|body|bust|videocall"） */
    filter_types: string;
    filter_count: number;
    result_count: number;
    /** バストサイズ指定時のみ付与するカップ数（noPreference時は付与しない） */
    bust_size?: FirebaseBustSize;
  };
  request_sent: {
    request_type: "voicecall" | "videocall" | "image" | "video";
    partner_id: string;
  };
  lovense_sent:
    | {
        surface: "call";
        control_type: FirebaseLovenseControlType;
        partner_id: string;
        call_id: string;
      }
    | {
        surface: "stream";
        control_type: FirebaseLovenseControlType;
        partner_id: string;
        broadcast_id: string;
      };
  // full_control を除く通常メニューが「未実行→実行中」に移った瞬間（購入者端末バイブ発火と同時）。
  // stream では自分・他人問わず誰かの実行開始で発火する
  lovense_execution_start:
    | {
        surface: "call";
        partner_id: string;
        call_id: string;
        duration_sec: number;
      }
    | {
        surface: "stream";
        partner_id: string;
        broadcast_id: string;
        duration_sec: number;
      };
  media_unlock_tap: {
    media_type: "image" | "video";
    partner_id: string;
    price_pt: number;
  };
  gift_select_tap: {
    gift_type: "gift" | "sticker";
    item_id: string;
    price_pt: number;
    partner_id: string;
  };
  reaction_add: {
    reaction_type: "like" | "favorite";
    object_type: "user" | "item";
    object_id: string;
  };
  reaction_remove: {
    reaction_type: "like" | "favorite";
    object_type: "user" | "item";
    object_id: string;
  };
  item_view: { item_id: string; seller_id: string };
  item_purchase_tap: { item_id: string; seller_id: string; price_yen: number };
  post_create: { post_type: "normal" | "onetap" };
  // 修正#2: noti_type 整備までは "noti_type_<値>" 形式（例: "noti_type_3"）
  push_open: { campaign_id: string };
  review_prompt_tap: { trigger: string };

  // §5 通話（call_id は Agora の channel 名をそのまま入れる）
  call_tap: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
  };
  call_result:
    | {
        call_id: string;
        call_type: FirebaseCallType;
        partner_id: string;
        result: Exclude<FirebaseCallResult, "error">;
        latency_sec: number;
      }
    | {
        call_id: string;
        call_type: FirebaseCallType;
        partner_id: string;
        result: "error";
        latency_sec: number;
        /** ストア/SDKが返す生のエラーコード（加工禁止） */
        error_code: string;
      };
  call_ended: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
    duration_sec: number;
    end_reason: FirebaseCallEndReason;
    ended_by: "self" | "partner";
  };
  call_incoming_view: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
  };

  // §6 ライブ配信（broadcast_id は Agora の channel 名。配信者/視聴者で同じ値になること）
  broadcast_start: { broadcast_id: string };
  broadcast_connected: { broadcast_id: string; latency_sec: number };
  broadcast_end: {
    broadcast_id: string;
    duration_sec: number;
    peak_audience_count: number;
    total_audience_count: number;
    end_reason: FirebaseBroadcastEndReason;
  };
  /**
   * 配信中（待機中を含む）の配信者の待機画面（プロフィール）を表示した（2026-10-07 共通辞書に追加。データチーム依頼）。
   * 配信一覧から配信者を押すと、視聴ボタンを押すまで配信は見られない。その手前で止まった人を取るため。
   * watch_type は無料視聴（free）か有料（paid）か。0 / 1 の数値は Carina iOS 1.7 がイベントごと捨てるため enum の文字列で送る
   */
  broadcast_preview_view: {
    broadcast_id: string;
    broadcaster_id: string;
    watch_type: FirebaseBroadcastWatchType;
  };
  // 待機画面で視聴ボタンを押した（入室の成否は問わない。入室確定は broadcast_enter）
  broadcast_watch_tap: {
    broadcast_id: string;
    broadcaster_id: string;
    watch_type: FirebaseBroadcastWatchType;
  };
  broadcast_enter: { broadcast_id: string; broadcaster_id: string };
  broadcast_exit: {
    broadcast_id: string;
    broadcaster_id: string;
    watch_sec: number;
    exit_reason: FirebaseBroadcastExitReason;
  };
  broadcast_comment_sent: {
    broadcast_id: string;
    broadcaster_id: string;
    char_count: number;
  };

  // §7 稼働状態
  state_heartbeat:
    | { state: "calling"; call_id: string }
    | { state: "broadcasting" | "viewing"; broadcast_id: string }
    | { state: "waiting" };
  availability_toggle: { new_state: FirebaseAvailabilityState };

  // §8 課金ファネル
  // 課金の開始/成功/失敗はBEの決済ログ（log_point）が正のため計測しない。
  // タブ別の離脱率・離脱プランは paywall_tab_view / purchase_plan_tap と
  // BEの成功ログ（log_web_payment_point / transaction_log）の user_id×時刻近接突合で算出する
  pt_zero_reached: { last_action: string };
  /**
   * scene: チャージシートがライブ配信中/通話中/チャット中等に開かれた場合のシーン。
   * 未指定 = 通常の /purchase ページから（trigger="self"時のみ意味を持つ。
   * trigger="pt_zero"時は直前の pt_zero_reached.last_action と同値になる）。
   */
  paywall_view: {
    trigger: FirebasePaywallTrigger;
    scene?: FirebasePurchaseScene;
  };
  /** 課金画面の初期表示（アクティブタブで1回）＋タブ切替時に1回。同値への再セットでは送らない */
  paywall_tab_view: { tab: FirebasePayTab };
  /**
   * タブ内のプランカードをタップした瞬間。PurchasePageTemplate / InsufficientPointSheet /
   * PurchaseQuickChargeSheet の3導線すべてから送信する（確認シート経由の購入も含む）。
   * 金額(円)は含めない。fallbackPriceJpyはIAPではストア実価格とズレうる表示用の固定値で、
   * 正確な金額が必要なら product_id からマスタで引ける。
   * scene: paywall_view と同義（PurchasePageTemplate 由来なら未指定）。
   */
  purchase_plan_tap: {
    tab: FirebasePayTab;
    product_id: string;
    price_pt: number;
    /** 何回目購入ボーナスのカードか。0=通常パッケージ、1〜5=対象のボーナス回数 */
    bonus_level: number;
    scene?: FirebasePurchaseScene;
  };

  /**
   * 課金画面へ誘導する入口（バナー・お知らせ）の操作（2026-11-02 追加。PR #1664。データチームの承認待ち）。
   * step: show=お知らせの本文シートを開いた / tap=バナー、またはシートの「確認する」をタップ。
   * 遷移先の paywall_view（trigger=campaign）と、課金画面の source で突き合わせられる。
   */
  purchase_promo_step: {
    promo: FirebasePurchasePromo;
    step: "show" | "tap";
  };

  /**
   * バナーのタップ（2026-11-02 追加。データチームの承認待ち）。
   * placement: 設置場所。banner_type: バナーの種類（BANNER_TYPES の値を snake_case にしたもの。
   * デイリーボーナスの固定バナーは deal / campaign、購入画面は purchase_*、
   * ポイントバックのカードは new_user_point_back / autumn_point_back）。
   * 送信は trackBannerTap（bannerTap.ts）を使う。
   * 表示（impression）は送らない。
   * さがす女性一覧の Apple Pay・クレカ決済バナーは purchase_promo_step 側で計測済みなので、これには含めない。
   */
  banner_tap: {
    placement: FirebaseBannerPlacement;
    banner_type: FirebaseBannerType;
  };

  // §10 高評価ユーザー紹介オンボーディング（2026-09-03追加。データチーム承認済み）
  /** オンボーディング無料デモ通話の着信バナーの状態遷移 */
  /**
   * 無料デモ通話の進み具合。offer_view / offer_tap は着信前の「体験する」案内（2026-10-15 追加）。
   * show は案内の確認ダイアログで「はい」を選び、着信した時点（2026-10-15 から。以前は自動着信の時点）
   */
  demo_call_banner_step: {
    step: "offer_view" | "offer_tap" | "show" | "answer" | "decline";
    /** coco+ のおすすめ枠 A/B（cocoplus_recommend）の群。coco+ のみ付く（2026-10-21 追加。データチーム了承済み・jambo-vault Context/03_プロダクト/Carina.md） */
    ab_variant?: FirebaseAbVariant;
    /** 入口の見た目。card=従来のカード（A群） / icon=今すぐ通話の行の丸アイコン（B群）。coco+ のみ付く */
    placement?: "card" | "icon";
  };
  /**
   * coco+ のさがす画面「おすすめ枠」（B群のみ。2026-10-21 追加。データチーム了承済み・jambo-vault Context/03_プロダクト/Carina.md）。
   * show は枠が ≥1 人で描画された時（さがす画面を開くごとに1回）、message_tap は「メッセージを送る」タップ。
   * position は枠内の 0 始まりの位置
   */
  cocoplus_recommend_step: {
    step: "show" | "message_tap";
    ab_variant: FirebaseAbVariant;
    recommend_count: number;
    partner_id?: string;
    position?: number;
  };
  /**
   * オンボーディング最後の ATT 許可説明シート（2026-10-15 追加）。
   * result は「次へ」の後に iOS の ATT ダイアログで答えた結果で、att_status が付く
   */
  att_prompt_step: {
    step: "show" | "next" | "later" | "result";
    att_status?: FirebaseAttStatus;
  };
  /**
   * push 通知許諾の事前案内シート（Carina / Sakura）。
   * result は「通知を受け取る」の後に OS ダイアログで答えた結果で、push_status が付く
   */
  push_prompt_step: {
    step: "show" | "slide_view" | "accept" | "decline" | "result";
    trigger: FirebasePushPromptTrigger;
    push_status?: FirebasePushStatus;
    /** カルーセルの何枚目か（1 始まり）。show は 1、slide_view は 2 枚目以降を初めて表示したとき */
    slide_index?: number;
  };
  /**
   * OS の push 通知許諾状態が変わった。Sina 独自追加（2026-10-20）。
   * os_dialog: アプリ内から出した OS ダイアログの結果（trigger 付き）。
   * os_settings: 設定アプリ等アプリ外での変更を起動・フォアグラウンド復帰時に検知
   * （elapsed_min = 前回この端末で旧状態を確認してからの経過分＝変更時刻の上限幅）。
   */
  push_permission_change: {
    push_status: FirebasePushStatus;
    change_via: "os_dialog" | "os_settings";
    trigger?: FirebasePushPermissionTrigger;
    elapsed_min?: number;
  };
  /**
   * アプリ内通知設定のトグル変更（Sina 独自追加 2026-10-20）。enabled は 0/1。
   * preference_match = 好みに合う女性からの通知（2026-10-28 追加・データチーム承認待ち）
   */
  notification_setting_change: {
    setting: "message" | "preference_match";
    enabled: 0 | 1;
  };
  /**
   * さがす画面→プロフィール→通話リクエストのオンボーディング進捗。
   * 「開始した人のうち何人が通話まで到達したか」は本イベントと call_result 等の
   * 通常の通話イベントを user_pseudo_id×時刻近接で突合して算出する
   * （Repro と異なり BigQuery エクスポートで後から結合できるため、専用のマーカー
   * イベントは追加しない）。
   */
  high_review_onboarding_step: {
    step: "start" | "reach_profile" | "skip" | "complete";
    /**
     * スキップUIのA/Bテスト群（2026-09-22追加）。実際に描画した群を載せる。
     * control = 現行UI（吹き出し右上・下線付き）、treatment = 控えめUI（吹き出し下部）。
     * 割り付けは BE の `enroll_high_review_onboarding_experiment` 頼みで非同期なため、
     * 確定前に送る step（主に start）では付かない。群別の分母は BE の到達ログを正本にする。
     */
    ab_variant?: FirebaseAbVariant;
  };

  // §11 チャット pt不足の先回りモーダル（2026-09-15追加）
  /**
   * 残高不足のまま次を送ろうとしないと課金導線が届かない層に、滞在中に先回りで出す
   * モーダルの状態遷移。show はモーダル自体の表示で、paywall_view は立たない
   * （charge_tap でチャージシートを開いた時点で従来どおり paywall_view / pt_zero_reached が立つ）。
   */
  /**
   * ビデオ通話が pt 不足で自動終了した直後に、汎用ポイントストアの代わりに出す
   * 文脈モーダルの状態遷移。
   *
   * このモーダルは従来の pt不足シートを**置き換える**もので、露出そのものは
   * 変えない。そのため pt_zero_reached / paywall_view は従来どおり同じ条件で
   * 立て続ける（露出を固定したまま「見せ方」の効果だけを分離して測るため）。
   */
  call_pt_shortage_prompt_step: {
    /**
     * show / charge_tap / dismiss はモーダル本体。
     * bonus_banner_show は CTA のあとに一瞬挟む初回購入限定コースのバナー画像（操作なし、
     * 一定時間でそのまま購入シートへ進む）。charge_tap と bonus_banner_show の差は
     * 初回コースが使えない人の分。
     * message_tap は coco+ の切れた直後（call_end）だけ出す「メッセージを送る」を押して
     * 会話画面へ移った（モーダルは閉じる。dismiss は立てない）。
     */
    step:
      | "show"
      | "charge_tap"
      | "dismiss"
      | "bonus_banner_show"
      | "message_tap";
    /**
     * モーダルを出したきっかけ。通話が切れた瞬間に出たのか、そのあと同じ相手を
     * 探しに来た瞬間に出たのかで刺さる文脈が違うため内訳を取る。
     * show だけでなく charge_tap / dismiss にも同じ値を載せるので、入口ごとの
     * CTA タップ率を show と突合せずに直接割れる。
     */
    trigger: "call_end" | "call_retap" | "chat_revisit" | "profile_revisit";
    /** 同一相手へのかけ直しを追うため。この施策の根拠がそこにある */
    partner_id: string;
    /**
     * 通話終了の詳細コード。Phase 1 は 201（pt 不足による自動終了）限定なので、
     * それ以外で発火していないかの検証に使う。100（自分で切った）に誤爆すると
     * 露出が変わって効果測定が壊れるため、ここは必ず載せる。
     */
    call_end_detail: number;
    /** Phase 1 は video 限定。将来 voice に広げたときに割れるよう最初から載せる */
    call_type: "video" | "voice";
    /** 表示時点の残高(pt)。「次の1分に足りない」判定が意図どおりかの検証用 */
    balance_pt?: number;
    /** 通話の継続秒数。文脈が育つ前に切れたケースを切り分ける */
    call_duration_sec?: number;
  };

  /**
   * 動画終了の先回り購入モーダル（画像動画ルート）。2 本目の動画を見終わって次の 1 本が
   * 開けなくなった新規に、届いている未開封の動画のサムネで購入導線を出す。
   * - show / charge_tap / dismiss はモーダル本体
   * - thumb_tap はサムネを押した（CTA と同じ動きで購入へ進む。slot にどの枚か、
   *   partner_id にその女性を載せる）
   * - bonus_banner_show は CTA のあとに一瞬挟む初回購入限定コースの画像
   * - gallery_landed は購入成功後にギャラリー（未開封タブ）へ着地した
   * - list_tap は「一覧で見る」を押して購入せずにギャラリーへ移った（シートは閉じる）
   * trigger は出たきっかけ:
   * - video_end: 2 本目の動画を見終わって次の 1 本が開けなくなった（購入導線）
   * - first_video_dwell: 1 本目の動画のあと 130pt 残したままアプリに 1 分とどまった
   *   （「他の動画を見る」。購入は出さず、charge_tap / thumb_tap はギャラリーへ移って購入確認を経て再生へ。
   *   2 本目を開いたかはサーバーの log_point で見るので、ここでは送らない）
   * - gallery_guide_show / gallery_guide_close: 何も押さずに閉じた人に、閉じたあと出す「ギャラリーもあるよ」
   *   （マイページ ＞ ギャラリーの場所の案内）を出した／閉じた。video_end・first_video_dwell の両方で送る
   */
  media_pt_shortage_prompt_step: {
    step:
      | "show"
      | "thumb_tap"
      | "charge_tap"
      | "dismiss"
      | "bonus_banner_show"
      | "gallery_landed"
      | "list_tap"
      | "gallery_guide_show"
      | "gallery_guide_close";
    trigger:
      | "video_end"
      | "profile_revisit"
      | "chat_revisit"
      | "first_video_dwell";
    /** 先頭サムネの女性。thumb_tap では押した枚の女性 */
    partner_id: string;
    /**
     * 表示時点の未開封動画数。2026-10-02 から送っていない（以後も数えない。Carina iOS 1.7 が 0 / 1 を
     * Bool とみなしてイベントごと捨てるため。9/30〜10/2 の分はそもそも届いていない）
     */
    unopened_count?: number;
    /**
     * 先頭サムネが直前に見た女性か（1 / 0）。いまは送っていない（TODO(carina-1.8)）。
     * Carina iOS 1.7 が 0 / 1 を Bool とみなしてイベントごと捨てるため、1.8 の配信後に戻す
     */
    is_last_partner?: 0 | 1;
    /**
     * thumb_tap のみ: 押した枚の位置（1 始まり）。いまは送っていない（TODO(carina-1.8)）。
     * 1 枚目（= 1）だと Carina iOS 1.7 がイベントごと捨てるため、1.8 の配信後に戻す
     */
    slot?: number;
  };
  /**
   * ルート進入前の先回りモーダル（Carina・登録 24 時間以内・未課金・まだ pt を一度も使っていない新規）。
   * 消費直前（layer=stop）か物色（layer=browse）の行動のあと 2 分なにもしなかった人に、
   * その行動をやり直すボタン 1 つのモーダルを出す。出さない対照群（holdout）は 2026-10-14 から 0%。
   * - eligible: 出す条件がすべて揃った（両群）。出さない条件（相手が待機中でない・投稿が無い・配信終了など）に
   *   当たったら両群とも送らない（群間で分母の定義を揃えるため）
   * - show / cta_tap / dismiss: モーダル本体（treatment のみ）
   * Carina iOS 1.7 は 0 / 1 のパラメータを Bool とみなしてイベントごと捨てるので、wait_sec 以外は文字列 enum。
   * wait_sec は 2 分ルールを通ったあとなので常に 120 以上（0 / 1 にならない）
   */
  preroute_prompt_step: {
    step: "eligible" | "show" | "cta_tap" | "dismiss";
    arm: "treatment" | "holdout";
    layer: "stop" | "browse";
    /**
     * stop: 止まった行動（media / call / chat / live / paywall。paywall は購入画面を見て買わずに離れた）。
     * browse: 最後に見た・いいねした相手の状態で決まる中身（live / video_call / voice_call / media / message）
     */
    trigger:
      | "media"
      | "call"
      | "chat"
      | "live"
      | "paywall"
      | "video_call"
      | "voice_call"
      | "message";
    partner_id: string;
    /** 最後の対象行動から eligible / 表示までの秒数 */
    wait_sec: number;
  };
  chat_pt_shortage_prompt_step: {
    step: "show" | "charge_tap" | "dismiss";
    /**
     * モーダルを出したきっかけ。チャット滞在で出たのか、相手プロフィールへ回って
     * から出たのか、いいねで出たのかで刺さる文脈が違うため内訳を取る。
     * show だけでなく charge_tap / dismiss にも同じ値を載せるので、入口ごとの
     * CTA タップ率を show と突合せずに直接割れる。
     */
    trigger?: "chat_dwell" | "profile_dwell" | "favorite";
    partner_id: string;
  };

  /**
   * 好みタイプ選択オンボーディングのファネル。登録直後に1回だけ通る画面。
   *
   * 段は効果測定ダッシュボード（jambo-ds-effect-dashboard の
   * `src/lib/prefOnboarding.ts` の PREF_ONBOARDING_STEPS）と1:1。**片方だけ足すと
   * 表に出ない段ができる。**
   *
   * - show: 画面が出た（母数。ここを分母に残存率を見る）
   * - intro_start: 入口で「好みを見つける」を押した（入口があるのは Sakura だけ。2026-10-22 追加）
   * - bridge: 橋渡し画面が出た（2026-10-22 追加）
   * - search: 「この条件でさがす」を押した
   * - result: 候補が返って一覧が出た
   * - result_empty: 候補が0件だった（result とは排他）
   * - search_error: 検索に失敗した
   * - like_send: 候補にいいねを送った（スワイプ・一覧とも。2026-10-22 追加）
   * - bubbles: いいねを送った子のバブル画面が出た（2026-10-22 追加）
   * - complete: 「アプリをはじめる」まで到達した（完走）
   *
   * 途中離脱は次の段が出ないことで判定し、専用イベントは持たない。
   */
  preference_onboarding_step: {
    step:
      | "show"
      | "intro_start"
      | "bridge"
      | "search"
      | "result"
      | "result_empty"
      | "search_error"
      | "like_send"
      | "bubbles"
      | "complete";
  };

  /**
   * 8問版の結果画面で、候補カード1枚ごとのいいね・パス（2026-10-22 追加。Sina 独自）。
   * position は候補の並びの 0 始まり。見直しで同じ子を2回目に見たときも送る
   */
  preference_onboarding_swipe: {
    action: "like" | "pass";
    position: number;
  };

  /**
   * 8問版の設問表示と戻る操作。8問の群は9問目に animal_face が出る（coco+ を除く）。
   * show は再表示時も送り、back の question は押下元
   */
  preference_onboarding_question_step: {
    step: "show" | "back";
    question: FirebasePreferenceOnboardingQuestion;
  };

  // §12 発信先が通話中のときのシート（2026-09-25追加。Sina 独自）
  /**
   * レコメンド経由のポイント消費は recommend_partner_id と後続の call_tap / chat_open /
   * BE の log_point をユーザー×時刻近接で突合して算出する（経由マーカーは持ち回らない）。
   */
  busy_call_sheet_step:
    | {
        step: "show" | "call_request_tap" | "message_tap" | "close";
        busy_partner_id: string;
        call_type: FirebaseCallType;
      }
    | {
        step: "recommend_show";
        busy_partner_id: string;
        call_type: FirebaseCallType;
        recommend_count: number;
      }
    | {
        step: "recommend_tap";
        busy_partner_id: string;
        call_type: FirebaseCallType;
        recommend_partner_id: string;
        /** 一覧の何番目か（0始まり） */
        recommend_position: number;
      };

  // §13 発信が繋がらなかった（無応答・拒否・キャンセル）ときの終了モーダルの他の女性（2026-09-25追加。Sina 独自）
  /** 通話中のときは busy_call_sheet_step が同じ内容を送る。並べて集計できるよう形を揃えてある */
  call_fallback_step:
    | {
        step: "recommend_show";
        trigger: FirebaseCallFallbackTrigger;
        /** 繋がらなかった相手 */
        partner_id: string;
        call_type: FirebaseCallType;
        recommend_count: number;
      }
    | {
        step: "recommend_tap";
        trigger: FirebaseCallFallbackTrigger;
        partner_id: string;
        call_type: FirebaseCallType;
        recommend_partner_id: string;
        /** 一覧の何番目か（0始まり） */
        recommend_position: number;
      };

  // §14 おかえりプラン（休眠復帰限定ポイント購入プロモーション）
  // メールは送らずプッシュ通知のみで呼び戻す方針に決定したため、仕様書記載のokaeri_mail_sent /
  // okaeri_mail_clickは対応しない。プッシュ通知は BE(JPNS) が noti_type=43 で送り、
  // 送信イベント okaeri_push_sent も BE が送る。eligible も日次バッチ（BE）側の
  // イベントで、このSPAからは送信しない（辞書として型だけ定義）。
  /** 対象抽出（7日目の日次バッチ）。BE専用 */
  okaeri_eligible: {
    tier: number;
    /** 直前30日課金額（円） */
    sales_amount_jpy: number;
    days_since_last_purchase: number;
    ab_variant: FirebaseAbVariant;
  };
  /** プッシュ通知（noti_type=43）のタップ。tier を渡さないアプリ（Sakura）では 0 */
  okaeri_push_open: { tier: number };
  /** 対象になってから最初のログイン */
  okaeri_return: {
    tier: number;
    days_since_eligible: number;
    referrer: "push" | "organic";
  };
  /** アプリ内でプランを表示（さがす画面のポップアップ／購入画面最上部） */
  okaeri_impression: {
    tier: number;
    placement: "in_app_popup" | "purchase_page_top";
  };
  /** 購入完了 */
  okaeri_purchase: {
    tier: number;
    price_jpy: number;
    payment_method: "iap" | "credit" | "paidy";
  };
  // §14 ストアレビューポップアップの表示依頼（2026-10-07追加。データチーム承認済み）
  /**
   * OS に SKStoreReviewController / AppStore.requestReview の表示を「依頼した」
   * タイミングで送る。Apple 側のレート制限により実際に表示されたかは分からない。
   */
  app_review_requested: { trigger: "purchase" | "review_lp" | "call" };
  // §15 秋のポイントバックキャンペーン（2026-10-28追加。Sina 独自。データチーム承認待ち）
  /**
   * キャンペーンの LP を開いた。LP には画面IDが無く現在画面が切り替わらないため、
   * source は遷移元の画面になる（どこから LP に入ったかが分かる）。
   */
  autumn_point_back_page_view: Record<string, never>;
  /** ポイントを受け取った（段ごとに1回）。ユーザー個人の獲得ポイントは point の合計 */
  autumn_point_back_reward: { target_count: number; point: number };
  /**
   * 対象の女性とのビデオ通話（通話が成立したとき。発信して繋がったとき（call_result=answered と同じタイミング）と、
   * 着信に応答して繋がったときの両方で、どの画面から始まった通話でも送る。繋がらなかった発信と、人数に入らない音声通話は含まない）。
   * 対象かどうかは、アプリが取得済みの対象の一覧で判定するため、一覧を取得していない状態での
   * 通話は、対象の女性でも送られない（取りこぼしになりうる）。
   */
  autumn_point_back_target_call: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
  };
};

export type FirebaseEventName = keyof FirebaseEventParamsMap;

/**
 * イベント名の定数。呼び出し側は文字列リテラルを直書きせず必ずこれを使う。
 * FirebaseEventParamsMap のキーと1:1（片方だけ更新すると型エラーになる）。
 */
export const FIREBASE_EVENT_NAME: {
  [K in FirebaseEventName as Uppercase<K>]: K;
} = {
  SIGNUP_START: "signup_start",
  SIGNUP_COMPLETE: "signup_complete",
  SIGNUP_FLOW_STEP: "signup_flow_step",
  SIGNUP_FLOW_CHOICE: "signup_flow_choice",
  PROFILE_EDIT: "profile_edit",
  PROFILE_VIEW: "profile_view",
  DISCOVER_SECTION_VIEW: "discover_section_view",
  MESSAGE_LIST_LIVE_TAP: "message_list_live_tap",
  PROFILE_AVATAR_TAP: "profile_avatar_tap",
  CHAT_OPEN: "chat_open",
  CHAT_INPUT_START: "chat_input_start",
  CHAT_SEND_TAP: "chat_send_tap",
  CHAT_INPUT_ABANDON: "chat_input_abandon",
  SEARCH_EXECUTE: "search_execute",
  REQUEST_SENT: "request_sent",
  LOVENSE_SENT: "lovense_sent",
  LOVENSE_EXECUTION_START: "lovense_execution_start",
  MEDIA_UNLOCK_TAP: "media_unlock_tap",
  GIFT_SELECT_TAP: "gift_select_tap",
  REACTION_ADD: "reaction_add",
  REACTION_REMOVE: "reaction_remove",
  ITEM_VIEW: "item_view",
  ITEM_PURCHASE_TAP: "item_purchase_tap",
  POST_CREATE: "post_create",
  PUSH_OPEN: "push_open",
  REVIEW_PROMPT_TAP: "review_prompt_tap",
  CALL_TAP: "call_tap",
  CALL_RESULT: "call_result",
  CALL_ENDED: "call_ended",
  CALL_INCOMING_VIEW: "call_incoming_view",
  BROADCAST_START: "broadcast_start",
  BROADCAST_CONNECTED: "broadcast_connected",
  BROADCAST_END: "broadcast_end",
  BROADCAST_PREVIEW_VIEW: "broadcast_preview_view",
  BROADCAST_WATCH_TAP: "broadcast_watch_tap",
  BROADCAST_ENTER: "broadcast_enter",
  BROADCAST_EXIT: "broadcast_exit",
  BROADCAST_COMMENT_SENT: "broadcast_comment_sent",
  STATE_HEARTBEAT: "state_heartbeat",
  AVAILABILITY_TOGGLE: "availability_toggle",
  PT_ZERO_REACHED: "pt_zero_reached",
  PAYWALL_VIEW: "paywall_view",
  PAYWALL_TAB_VIEW: "paywall_tab_view",
  PURCHASE_PLAN_TAP: "purchase_plan_tap",
  PURCHASE_PROMO_STEP: "purchase_promo_step",
  BANNER_TAP: "banner_tap",
  DEMO_CALL_BANNER_STEP: "demo_call_banner_step",
  COCOPLUS_RECOMMEND_STEP: "cocoplus_recommend_step",
  ATT_PROMPT_STEP: "att_prompt_step",
  PUSH_PROMPT_STEP: "push_prompt_step",
  PUSH_PERMISSION_CHANGE: "push_permission_change",
  NOTIFICATION_SETTING_CHANGE: "notification_setting_change",
  HIGH_REVIEW_ONBOARDING_STEP: "high_review_onboarding_step",
  CHAT_PT_SHORTAGE_PROMPT_STEP: "chat_pt_shortage_prompt_step",
  CALL_PT_SHORTAGE_PROMPT_STEP: "call_pt_shortage_prompt_step",
  MEDIA_PT_SHORTAGE_PROMPT_STEP: "media_pt_shortage_prompt_step",
  PREROUTE_PROMPT_STEP: "preroute_prompt_step",
  PREFERENCE_ONBOARDING_STEP: "preference_onboarding_step",
  PREFERENCE_ONBOARDING_QUESTION_STEP: "preference_onboarding_question_step",
  PREFERENCE_ONBOARDING_SWIPE: "preference_onboarding_swipe",
  BUSY_CALL_SHEET_STEP: "busy_call_sheet_step",
  CALL_FALLBACK_STEP: "call_fallback_step",
  OKAERI_ELIGIBLE: "okaeri_eligible",
  OKAERI_PUSH_OPEN: "okaeri_push_open",
  OKAERI_RETURN: "okaeri_return",
  OKAERI_IMPRESSION: "okaeri_impression",
  OKAERI_PURCHASE: "okaeri_purchase",
  APP_REVIEW_REQUESTED: "app_review_requested",
  AUTUMN_POINT_BACK_PAGE_VIEW: "autumn_point_back_page_view",
  AUTUMN_POINT_BACK_REWARD: "autumn_point_back_reward",
  AUTUMN_POINT_BACK_TARGET_CALL: "autumn_point_back_target_call",
};
